import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import moment from 'moment'
import R from 'ramda'
import PropTypes from 'prop-types'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import DemographicsActions from '../../Data/Redux/DemographicsRedux'
import Tool from '../../Components/ReportsComponents/Tool'
import CustomizedLegend from '../../Components/ReportsComponents/CustomizedLegend'
import ReportTemplate from './ReportTemplate'
import CentersInfoActions from '../../Data/Redux/CentersInfoRedux'
import CentersExcelActions from '../../Data/Redux/CentersExcelRedux'

const COLORS = ['#152940', '#4B3F72', '#904C77', '#119DA4', '#53A2FF']

class Demographics extends Component {
  state = {
    width: 0,
    graphData: [],
  }

  componentWillMount() {
    const { activeCenterId, fromCenter, getCentersInfo, getDemographics,
      match: { params }, setCenter } = this.props

    if (fromCenter) {
      if (params) {
        setCenter(params.centerId)
        getCentersInfo(params.centerId, 'demographics')
      } else if (activeCenterId !== 0) {
        getCentersInfo(activeCenterId, 'demographics')
      }
    } else {
      getDemographics()
    }
  }

  componentDidMount() {
    this.updateWindowDimensions()
    window.addEventListener('resize', this.updateWindowDimensions)
  }

  componentWillReceiveProps(nextProps) {
    const newState = { ...this.state }
    const { demographics, centers, study_programs, centersInfo } = nextProps

    if (!demographics || !centers || !centersInfo) {
      return null
    }

    let data = {}
    let isCenters = false

    if (this.props.userRole === 'Director' && study_programs.length) {
      data = demographics
    } else if (!R.isEmpty(centersInfo)) {
      data = centersInfo
    } else {
      data = demographics
      isCenters = true
    }

    newState.graphData = Object.keys(data).map((date) => {
      let total = 0
      const results = Object.keys(data[date]).map((centerOrProgramId) => {
        let graphLabel = null

        if (isCenters) {
          graphLabel = centers.find(center => center.id === Number(centerOrProgramId)).abbreviation
        } else {
          graphLabel = study_programs.find(program => program.id === Number(centerOrProgramId)).name
        }

        const programTotal = data[date][centerOrProgramId]

        total += programTotal

        return {
          [graphLabel]: programTotal,
        }
      })

      return {
        day: date,
        formattedDate: moment(date).format('MMM YY'),
        ...Object.assign({}, ...results),
        total,
      }
    })

    return this.setState(newState)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions)
  }

  getBars = () => {
    const { centers, study_programs, userRole } = this.props

    if (userRole === 'Director') {
      return study_programs.map((program, index) => (
        <Bar
          key={program.id}
          dataKey={program.name}
          stackId="a"
          fill={this.pickColor(program.name) ? COLORS[index % COLORS.length] : 'gray'}
        />
      ))
    }

    return centers.map((center, index) => (
      <Bar
        key={center.id}
        dataKey={center.abbreviation}
        stackId="a"
        fill={this.pickColor(center.abbreviation) ? COLORS[index % COLORS.length] : 'gray'}
      />
    ))
  }

  updateWindowDimensions = () => this.setState({ width: window.innerWidth })

  filter = ({ currentTarget: { id: abbreviation } }) => {
    const newState = { ...this.state }
    const { demographics, centers, userRole, study_programs } = this.props
    let itemId = 0

    if (userRole === 'Director') {
      itemId = study_programs.find(program => program.name === abbreviation).id
    } else {
      itemId = centers.find(center => center.abbreviation === abbreviation).id
    }

    newState.graphData = newState.graphData.map((stateData) => {
      const data = { ...stateData }
      const { day: date } = data
      const monthData = demographics[date]
      const selectedCenterOrProgramId =
        Object.keys(monthData).find(key => Number(key) === Number(itemId))
      const itemTotal = monthData[selectedCenterOrProgramId]

      if (data[abbreviation]) {
        data[abbreviation] = null // Hide the data from the graph
        data.total -= itemTotal || 0
      } else {
        data[abbreviation] = itemTotal
        data.total += itemTotal || 0
      }

      return data
    })

    this.setState(newState)
  }

  pickColor = name => this.state.graphData.filter(value => value[name]).length

  download = () => {
    const { activeCenterId, fromCenter, getCentersExcel, getDemographicsExcel } = this.props

    if (fromCenter) {
      return getCentersExcel('demographics', activeCenterId)
    }

    return getDemographicsExcel()
  }

  render() {
    const { centers, userRole, fromCenter } = this.props
    const { width, graphData } = this.state
    const isResponsiveSize = width < 763

    return (
      <ReportTemplate
        title={(userRole === 'Director' || fromCenter) ? 'Niños Activos / Programa de estudio' : 'Niños Activos / Centro'}
        date={graphData[0] &&
          { initial: graphData[0].day, end: graphData[graphData.length - 1].day }}
        onClick={this.download}
        loading={!(graphData[0] && centers.length > 0)}
      >
        {graphData[0] && centers.length > 0 &&
          <BarChart
            width={isResponsiveSize ? 400 : 648}
            height={isResponsiveSize ? 300 : 400}
            data={graphData}
            margin={{ top: 40, right: 50, left: 10, bottom: 40 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="formattedDate" />
            <YAxis />
            <Tooltip content={<Tool withoutCurrency large />} />
            <Legend content={<CustomizedLegend onClick={!fromCenter && this.filter} />} />
            {this.getBars()}
          </BarChart>
        }
      </ReportTemplate>
    )
  }
}

Demographics.propTypes = {
  /** The ID of the active center */
  activeCenterId: PropTypes.number.isRequired,
  /** Array of available Advenio centers */
  centers: PropTypes.array.isRequired,
  /** Center information */
  centersInfo: PropTypes.array.isRequired,
  /** The data to be displayed in the Demographics graph */
  demographics: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  /** Flag marking whether the reports are being viewed within a Center */
  fromCenter: PropTypes.bool,
  /** React router path information */
  match: PropTypes.object.isRequired,
  /** Array of available study programs for the active center */
  study_programs: PropTypes.array.isRequired,
  /** The authenticated User's role */
  userRole: PropTypes.string.isRequired,

  /** Download excel spreadsheet for the specified report type from the API */
  getCentersExcel: PropTypes.func.isRequired,
  /** Fetch the information of the given center from the API */
  getCentersInfo: PropTypes.func.isRequired,
  /** Fetch the data for the demographics graph from the API */
  getDemographics: PropTypes.func.isRequired,
  /** Download the demographics report excel spreadsheet from the API */
  getDemographicsExcel: PropTypes.func.isRequired,
  /** Set the ID of the active center in the Redux state */
  setCenter: PropTypes.func.isRequired,
}

Demographics.defaultProps = {
  fromCenter: false,
}

const mapStateToProps = ({ centersInfo, ...state }) => ({
  demographics: state.demographics.get.results,
  centers: state.centerOptions.get.results,
  userRole: state.auth.user.role,
  study_programs: state.study_programs.get.results,
  centersInfo: centersInfo.demographics.results,
  activeCenterId: centersInfo.center,
})

const mapDispatchToProps = dispatch => ({
  getDemographics: () => dispatch(DemographicsActions.getRequest({}, true)),
  getDemographicsExcel: () => dispatch(DemographicsActions.DemographicsExcelRequest()),
  getCentersExcel: (path, centerId) =>
    dispatch(CentersExcelActions.CentersExcelRequest(path, centerId)),
  getCentersInfo: (id, path) => dispatch(CentersInfoActions.getCentersInfoRequest(id, path)),
  setCenter: id => dispatch(CentersInfoActions.setCenter(id)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Demographics))



// WEBPACK FOOTER //
// ./src/Containers/Reports/Demographics.jsx